import { useCurrentUserId } from 'modules/Auth/hooks';
import { useMemo, useEffect, useRef } from 'react';
import { useQueryTE, queryKeys } from 'utils/react-query';
import { getCreditCardsTE } from 'api/users/creditCards';
import { errorToReactLeft } from 'utils/fp';
import { css, cx } from '@linaria/core';
import { baseTheme, flex } from 'styles/utils';
import { Select, SelectOption } from 'components/Select';
import Image from 'next/image';
import { Pill } from 'components/Pill';
import { IconPlus } from 'components/Icons';
import { DotLoader } from 'components/DotLoader';
import { CARD_TYPE_ICON_MAPPING } from '../CreditCard/CardTypesIconMapping';
import { AddNewCard } from '../CreditCard/AddNewCard';
type Props = {
  onPaymentMethodChange?: (card: CreditCardOption) => void;
  selectedPaymentMethodId?: string;
  label?: string;
  required?: boolean;
};
type CreditCardOption = {
  value: string;
  name: string;
  content: JSX.Element;
  metaData?: CF.API.Users.Card;
};
const CreditCard = ({
  card
}: {
  card: CF.API.Users.Card;
}): JSX.Element => {
  return <>
      <div className={cx(flex.init, flex.alignItems.center, flex.gap[1], creditCardContainer)}>
        <Image src={CARD_TYPE_ICON_MAPPING[(card?.brand as keyof typeof CARD_TYPE_ICON_MAPPING)] ?? CARD_TYPE_ICON_MAPPING.DefaultCard} alt={card?.brand} width={24} height={24} />
        <span className={cardNumber}>****{card?.four_digits}</span>
        {card.default ? <Pill className="pill">Default</Pill> : null}
        <span className={cx(cardNumber, expirationDate)}>
          Expires {card?.exp_month}/{card?.exp_year}
        </span>
      </div>
    </>;
};
const renderNewCard: () => CreditCardOption = () => {
  return {
    value: 'newCard',
    name: 'newCard',
    content: <AddNewCard className={cx(newCardStyle)}>
        <IconPlus size={20} />
        Add new Card{' '}
      </AddNewCard>
  };
};
export const PaymentMethod = ({
  onPaymentMethodChange,
  selectedPaymentMethodId,
  label = 'Payment Method',
  required = true
}: Props) => {
  const userId = useCurrentUserId();
  const {
    data,
    isFetching
  } = useQueryTE([queryKeys.UserCreditCards, {
    userId
  }], getCreditCardsTE({
    userId
  }, errorToReactLeft));
  const creditCards: CF.API.Users.Card[] = data?._tag === 'Right' && data.right.cards ? data.right.cards : [];
  const hasInitiallyFired = useRef(false);
  const mapCardToOption = (card: CF.API.Users.Card): CreditCardOption => ({
    value: card.id,
    name: card.id,
    content: <CreditCard card={card} />,
    metaData: card
  });
  const options: CreditCardOption[] = useMemo(() => {
    return [renderNewCard(), ...creditCards?.map(mapCardToOption)];
  }, [creditCards]);
  const selectedCard = useMemo(() => {
    const defaultCard = options.find(option => option.metaData?.default);
    return options.find(option => option.value === selectedPaymentMethodId) ?? defaultCard ?? options?.[0];
  }, [options, selectedPaymentMethodId]);
  useEffect(() => {
    if (selectedCard && onPaymentMethodChange && !isFetching && !hasInitiallyFired.current) {
      hasInitiallyFired.current = true;
      onPaymentMethodChange(selectedCard);
    }
  }, [selectedCard, onPaymentMethodChange, isFetching]);
  const handlePaymentMethodChange = (card: SelectOption<Record<string, unknown>> | (SelectOption<Record<string, unknown>> & CreditCardOption)) => {
    if (card && onPaymentMethodChange) {
      onPaymentMethodChange((card as CreditCardOption));
    }
  };
  return <div className={paymentMethodContainer}>
      <label htmlFor="payment-method">
        {label} {required && <span className="asterisk">*</span>}
      </label>
      {isFetching ? <DotLoader text="Loading payment methods" /> : options.length === 0 ? <p className={cx(cardNumber, 'error')}>No credit cards available. Please add a credit card.</p> : <Select title={selectedCard?.metaData ? <CreditCard card={selectedCard.metaData} /> : <span>Select a card</span>} modal onChange={handlePaymentMethodChange} options={options} menuWrapperClass={cx(cardNumber)} />}
    </div>;
};
const paymentMethodContainer = "odules__User__components__Settings__Billing__PaymentMethod__index_tsx-paymentMethodContainer-psdmstz";
export const cardNumber = "odules__User__components__Settings__Billing__PaymentMethod__index_tsx-cardNumber-c1djk97y";
const creditCardContainer = "odules__User__components__Settings__Billing__PaymentMethod__index_tsx-creditCardContainer-c1y3emkp";
const expirationDate = "odules__User__components__Settings__Billing__PaymentMethod__index_tsx-expirationDate-e18c0u7w";
const newCardStyle = "odules__User__components__Settings__Billing__PaymentMethod__index_tsx-newCardStyle-n14rc83s";

require("./index.linaria.module.css!=!../../../../../../node_modules/@linaria/webpack-loader/node_modules/@linaria/webpack5-loader/lib/outputCssLoader.js!./index.tsx");